// Admin "All proformas" — every quotation in every state (draft, pending
// approval, rejected, approved, sent, accepted, declined, expired,
// superseded), superseded revisions included, so the admin sees a
// quotation's full history and not just its latest version.
//
// Ledger-style: one ruled table, no card-per-row, no sidebar (root
// CLAUDE.md), same shape as AllRequestsPage/ApprovalQueuePage. Filter,
// sort and search are client-side over the whole list (the org and
// salesperson filter options are derived from the data itself — no extra
// endpoint). Admin-only: RequireRole here is UX only; the real gate is
// requireRole('admin') on GET /admin/quotations/all-proformas.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.jsx';
import { useAllProformas } from '../hooks/useAllProformas.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import { statusLabel } from '../i18n.js';
import { formatDate, toEatDateString } from '../utils/formatDate.js';
import { formatMoney } from 'shared/money.js';
import { useSortableList } from 'shared/hooks/useSortableList.js';
import { SortHeader } from 'shared/components/SortHeader/index.js';
import styles from './AllProformasPage.module.css';

const STATUSES = [
  'draft',
  'pending_approval',
  'rejected',
  'approved',
  'sent',
  'accepted',
  'declined',
  'expired',
  'superseded',
];

// key: the row field to sort on; numeric: compare as numbers, not strings.
// The compare logic itself (null-sorts-last, numeric vs lower-cased string)
// now lives in shared/hooks/useSortableList.js — this page only supplies
// its own column -> field mapping.
const SORT_COLUMNS = {
  proforma: { key: 'proforma_no' },
  organization: { key: 'organization_name' },
  pricedBy: { key: 'priced_by_name' },
  status: { key: 'status' },
  total: { key: 'total', numeric: true },
  validUntil: { key: 'valid_until' },
  revision: { key: 'revision', numeric: true },
  created: { key: 'created_at' },
};

export default function AllProformasPage() {
  const { t, lang } = useLanguage();
  const { data: proformas, isLoading, isError, error, refetch } = useAllProformas();

  const [status, setStatus] = useState('all');
  const [organization, setOrganization] = useState('all');
  const [pricedBy, setPricedBy] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const { sortCol, sortDir, handleSort, sortRows } = useSortableList(
    SORT_COLUMNS,
    'created',
    'desc'
  );

  const all = useMemo(() => proformas || [], [proformas]);

  // Filter options straight from the data — every organization / salesperson
  // that actually has a quotation, nothing more.
  const orgOptions = useMemo(() => {
    const seen = new Map();
    for (const q of all) {
      if (q.organization_name && !seen.has(q.organization_name)) {
        seen.set(q.organization_name, q.organization_name);
      }
    }
    return [...seen.keys()].sort((a, b) => a.localeCompare(b));
  }, [all]);

  const pricerOptions = useMemo(() => {
    const seen = new Set();
    for (const q of all) if (q.priced_by_name) seen.add(q.priced_by_name);
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [all]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = all.filter((q) => {
      if (status !== 'all' && q.status !== status) return false;
      if (organization !== 'all' && q.organization_name !== organization) return false;
      if (pricedBy === '__none__' && q.priced_by_name) return false;
      if (pricedBy !== 'all' && pricedBy !== '__none__' && q.priced_by_name !== pricedBy) {
        return false;
      }
      // Timezone bug fix (2026-10-25, flagged real in production, not
      // hypothetical, per Ermiyas): this used to be
      // `q.created_at.slice(0, 10)`, reading the UTC calendar date out
      // of created_at's ISO string. This server's actual OS timezone is
      // UTC+3 (not the UTC docs/DEPLOY.md assumes by default), so a
      // proforma created shortly after local midnight — a normal,
      // common case, not an edge case — was filed under the PREVIOUS
      // day in this filter, even though every user of this system is in
      // that same UTC+3 day. toEatDateString does the same explicit
      // Africa/Addis_Ababa conversion formatDate already uses elsewhere
      // on this page (e.g. the Valid Until column below), so the
      // day-boundary logic here matches that convention instead of
      // reading the UTC day out of a raw timestamp string. created_at
      // itself isn't rendered as its own column on this screen — this
      // only feeds the dateFrom/dateTo filter and the "created" sort
      // key, neither of which show the raw string to the user.
      const createdDateEat = q.created_at ? toEatDateString(q.created_at) : null;
      if (dateFrom && (!createdDateEat || createdDateEat < dateFrom)) return false;
      if (dateTo && (!createdDateEat || createdDateEat > dateTo)) return false;
      if (term) {
        const hay = `${q.proforma_no || ''} ${q.request_ref_no || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
    return sortRows(filtered);
  }, [all, status, organization, pricedBy, dateFrom, dateTo, search, sortCol, sortDir, sortRows]);

  const anyFilterActive =
    status !== 'all' ||
    organization !== 'all' ||
    pricedBy !== 'all' ||
    Boolean(dateFrom) ||
    Boolean(dateTo) ||
    Boolean(search.trim());

  const isTrulyEmpty = !isLoading && !isError && all.length === 0;
  const isFilteredEmpty = !isLoading && !isError && all.length > 0 && rows.length === 0;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('allProformas.title')}</h1>
      <p className={styles.subtitle}>{t('allProformas.subtitle')}</p>

      <div className={styles.filters}>
        <input
          type="search"
          className={styles.searchInput}
          placeholder={t('allProformas.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t('allProformas.searchPlaceholder')}
        />

        <label className={styles.filterField}>
          <span className={styles.filterLabel}>{t('allProformas.filter.status')}</span>
          <select
            className={styles.filterSelect}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="all">{t('allProformas.filter.allStatuses')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s, lang)}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.filterField}>
          <span className={styles.filterLabel}>{t('allProformas.filter.organization')}</span>
          <select
            className={styles.filterSelect}
            value={organization}
            onChange={(e) => setOrganization(e.target.value)}
          >
            <option value="all">{t('allProformas.filter.allOrganizations')}</option>
            {orgOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.filterField}>
          <span className={styles.filterLabel}>{t('allProformas.filter.pricedBy')}</span>
          <select
            className={styles.filterSelect}
            value={pricedBy}
            onChange={(e) => setPricedBy(e.target.value)}
          >
            <option value="all">{t('allProformas.filter.allSalespeople')}</option>
            <option value="__none__">{t('allProformas.filter.unpriced')}</option>
            {pricerOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.filterField}>
          <span className={styles.filterLabel}>{t('allProformas.filter.dateFrom')}</span>
          <input
            type="date"
            className={`${styles.filterSelect} tabularNums`}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </label>

        <label className={styles.filterField}>
          <span className={styles.filterLabel}>{t('allProformas.filter.dateTo')}</span>
          <input
            type="date"
            className={`${styles.filterSelect} tabularNums`}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </label>
      </div>

      {isLoading && <LoadingState variant="rows" rows={8} />}
      {isError && !isLoading && (
        <ErrorState messageId="allProformas.errorBody" error={error} onRetry={refetch} />
      )}
      {isTrulyEmpty && (
        <EmptyState titleId="allProformas.emptyTitle" bodyId="allProformas.emptyBody" />
      )}
      {isFilteredEmpty && (
        <EmptyState
          titleId="allProformas.emptyFilteredTitle"
          bodyId="allProformas.emptyFilteredBody"
        />
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <>
          <p className={styles.count}>
            {t(anyFilterActive ? 'allProformas.countFiltered' : 'allProformas.count', {
              shown: rows.length,
              total: all.length,
            })}
          </p>
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr className={styles.headerRow}>
                  <SortHeader
                    column="proforma"
                    label={t('allProformas.col.proforma')}
                    sortCol={sortCol}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    column="organization"
                    label={t('allProformas.col.organization')}
                    sortCol={sortCol}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    column="pricedBy"
                    label={t('allProformas.col.pricedBy')}
                    sortCol={sortCol}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    column="status"
                    label={t('allProformas.col.status')}
                    sortCol={sortCol}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    column="total"
                    label={t('allProformas.col.total')}
                    sortCol={sortCol}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    column="validUntil"
                    label={t('allProformas.col.validUntil')}
                    sortCol={sortCol}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    column="revision"
                    label={t('allProformas.col.revision')}
                    sortCol={sortCol}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {rows.map((q) => (
                  <tr key={q.id} className={styles.row}>
                    <td>
                      <Link to={`/all-proformas/${q.id}`} className={styles.proformaCell}>
                        <span className={`${styles.proformaNo} tabularNums`}>
                          {q.proforma_no || (
                            <span className={styles.notAllocated}>
                              {t('allProformas.notAllocated')}
                            </span>
                          )}
                        </span>
                        <span className={`${styles.subRef} tabularNums`}>{q.request_ref_no}</span>
                      </Link>
                    </td>
                    <td>
                      {lang === 'am' && q.organization_name_am
                        ? q.organization_name_am
                        : q.organization_name}
                    </td>
                    <td>{q.priced_by_name || t('allProformas.unpriced')}</td>
                    <td>{statusLabel(q.status, lang)}</td>
                    <td className="tabularNums">
                      {formatMoney(q.total)} {q.currency}
                    </td>
                    <td className="tabularNums">
                      {q.valid_until ? formatDate(q.valid_until, lang) || '—' : '—'}
                    </td>
                    <td className="tabularNums">{q.revision}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
